import { MATCH_SIZES, WAR_LAUNCH_MS, regionName } from '@tb/shared';
import type { AdmissionRefusal, WarSummary } from '@tb/shared';

export interface WarCall {
  kicker: string;
  heading: string;
  detail: string;
  cta: string;
  open: boolean;
}

/**
 * Why the war turned this commander away, said on the Play plate: by the
 * server's code, in the lobby's words, and its own sentence for anything new.
 */
export function refusalNotice(refusal: { code: AdmissionRefusal; message: string }): string {
  switch (refusal.code) {
    case 'WAR_FULL': return 'All cities are commanded. Join the next war, or train while you wait.';
    case 'MATCH_LOCKED_ENDGAME': return 'Endgame: no new commanders.';
    case 'NO_WAR_SLOT': return 'No war available. Try again shortly.';
    default: return refusal.message;
  }
}

/** Presentation of public admissions only. The server still decides the seat. */
export function warCall(war: WarSummary | null, launchSeconds = 0): WarCall {
  if (!war) return {
    kicker: 'Awaiting mobilisation', heading: 'Start the call-up',
    detail: `${MATCH_SIZES[MATCH_SIZES.length - 1]} cities a side · ${WAR_LAUNCH_MS / 1000}s call-up. NPCs fill open seats.`,
    cta: 'Start a full-scale war', open: true,
  };
  if (war.state === 'COMPLETE') return {
    kicker: 'War concluded', heading: 'The exchange is over',
    detail: 'Waiting for the next war.',
    cta: 'Deployments closed', open: false,
  };
  if (war.state === 'LOCKED_ENDGAME') return {
    kicker: 'Admissions closed', heading: 'The war is in its endgame',
    detail: 'No new commanders or spectators. Join the next war.',
    cta: 'Deployments closed', open: false,
  };
  if (!war.admissions) return {
    kicker: 'Admissions closed', heading: 'Every city commanded',
    detail: 'No seats available. Join the next war.',
    cta: 'Deployments closed', open: false,
  };
  if (war.state === 'LAUNCHING') return {
    kicker: 'War starting', heading: `Deployment in ${Math.max(0, launchSeconds)}`,
    detail: 'Region A opens first. NPCs fill open seats.',
    cta: 'Join the call-up', open: true,
  };
  if (war.state === 'MOBILISING') return {
    kicker: 'Regions mobilising', heading: `Round ${String(war.round).padStart(2, '0')} / ${war.phase}`,
    detail: `Join region ${regionName(war.activeRegions)} as round ${war.activeRegions + 1} begins, or take over an NPC-held city if the region is full.`,
    cta: 'Join the war', open: true,
  };
  return {
    kicker: 'War in progress', heading: `Round ${String(war.round).padStart(2, '0')} / ${war.phase}`,
    detail: 'You will take command of an NPC-held city.',
    cta: 'Join the war', open: true,
  };
}
